import { motion } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';
import translations from '../translations';

const serviceIcons = {
  sightline: (
    <svg xmlns="http://www.w3.org/2000/svg" className="w-8 h-8" viewBox="0 0 24 24" fill="none" stroke="#97d700" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" />
      <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
      <line x1="2" y1="12" x2="22" y2="12" />
    </svg>
  ),
  tms: (
    <svg xmlns="http://www.w3.org/2000/svg" className="w-8 h-8" viewBox="0 0 24 24" fill="none" stroke="#97d700" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
      <path d="M9 12l2 2 4-4" />
    </svg>
  ),
  aed: (
    <svg xmlns="http://www.w3.org/2000/svg" className="w-8 h-8" viewBox="0 0 24 24" fill="none" stroke="#97d700" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
    </svg>
  ),
  cloud: (
    <svg xmlns="http://www.w3.org/2000/svg" className="w-8 h-8" viewBox="0 0 24 24" fill="none" stroke="#97d700" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M18 10h-1.26A8 8 0 1 0 9 20h9a5 5 0 0 0 0-10z" />
    </svg>
  ),
  atlas: (
    <svg xmlns="http://www.w3.org/2000/svg" className="w-8 h-8" viewBox="0 0 24 24" fill="none" stroke="#97d700" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z" />
      <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z" />
    </svg>
  ),
  insight: (
    <svg xmlns="http://www.w3.org/2000/svg" className="w-8 h-8" viewBox="0 0 24 24" fill="none" stroke="#97d700" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <line x1="18" y1="20" x2="18" y2="10" />
      <line x1="12" y1="20" x2="12" y2="4" />
      <line x1="6" y1="20" x2="6" y2="14" />
    </svg>
  ),
};

const Cards = () => {
  const { lang } = useLanguage();
  const t = translations[lang];
  const isRTL = lang === 'ar';

  const services = [
    { key: 'svc1', icon: serviceIcons.sightline },
    { key: 'svc2', icon: serviceIcons.tms },
    { key: 'svc3', icon: serviceIcons.aed },
    { key: 'svc4', icon: serviceIcons.cloud },
    { key: 'svc5', icon: serviceIcons.atlas },
    { key: 'svc6', icon: serviceIcons.insight },
  ];

  return (
    <section className="bg-[#111820] py-20 md:py-28 px-6" id="services" dir={isRTL ? 'rtl' : 'ltr'}>
      <div className={`max-w-6xl mx-auto ${isRTL ? 'font-arabic' : ''}`}>
        {/* Section heading */}
        <div className="text-center mb-12 md:mb-16">
          <motion.h2
            className="text-[#97d700] text-4xl font-bold font-magistral mb-4"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            {t.services_title}
          </motion.h2>
          <div className="w-16 h-1 bg-[#97d700] mx-auto mb-6" />
          <motion.p
            className="text-gray-300 text-base md:text-lg max-w-2xl mx-auto leading-[1.9]"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            {t.services_subtitle}
          </motion.p>
        </div>

        {/* 3×2 grid desktop, 2-col tablet, 1-col mobile */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {services.map((svc, i) => (
            <motion.div
              key={svc.key}
              className="bg-[#1a2530] rounded-xl p-6 md:p-8 flex flex-col h-full border border-white/5 hover:border-[#97d700]/30 transition-all duration-300 group"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: i * 0.08 }}
            >
              {/* Icon */}
              <div className="w-14 h-14 rounded-lg bg-[#97d700]/10 flex items-center justify-center mb-5 group-hover:bg-[#97d700]/20 transition-colors duration-300">
                {svc.icon}
              </div>

              {/* Product name (English, Magistral) */}
              <h3 className="text-white text-xl font-bold font-magistral mb-1">
                {t[`${svc.key}_name`]}
              </h3>

              {/* Arabic label */}
              <p className="text-[#97d700] text-sm font-semibold mb-3">
                {t[`${svc.key}_label`]}
              </p>

              {/* Description */}
              <p className="text-gray-300 text-base leading-[1.9] flex-grow">
                {t[`${svc.key}_desc`]}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Cards;
